import { useState, useEffect } from "react";
import { getPhotoUrl } from '@/utils/photoUrl';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  Trophy,
  TrendingUp,
  User,
  Filter,
  GitCompare,
  BarChart3,
  Hand
} from "lucide-react";
import { useLanguage } from "@/i18n";

const POSITIONS = ["Todas", "Ponteiro", "Oposto", "Central", "Líbero"];
const POSITION_OPTIONS = ["Levantador", "Ponteiro", "Oposto", "Central", "Líbero"];
const SKILLS = ["ataque", "passe", "defesa", "saque", "bloqueio", "toque", "skill"];
const SKILL_LABELS = {
  ataque: "Ataque",
  passe: "Passe", 
  defesa: "Defesa",
  saque: "Saque",
  bloqueio: "Bloqueio",
  toque: "Toque",
  skill: "Skill"
};

export default function RankingPage({ evaluator }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [players, setPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [positionFilter, setPositionFilter] = useState("Todas");
  const [activeTab, setActiveTab] = useState("ranking");
  
  // Compare state
  const [player1, setPlayer1] = useState("");
  const [player2, setPlayer2] = useState("");
  const [position1, setPosition1] = useState("");
  const [position2, setPosition2] = useState("");
  const [comparison, setComparison] = useState(null);
  const [isComparing, setIsComparing] = useState(false);

  useEffect(() => {
    fetchRanking();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRanking = async () => {
    try {
      const [averagesRes, playersRes, evaluationsRes] = await Promise.all([
        axios.get(`${API}/players/all/averages`),
        axios.get(`${API}/players`),
        axios.get(`${API}/evaluations`)
      ]);
      
      // Criar mapa de jogadores para acessar position2
      const playerMap = {};
      playersRes.data.forEach(p => {
        playerMap[p.id] = p;
      });
      
      // Criar mapa de avaliações por jogador e posição avaliada
      const evalsByPlayerAndPosition = {};
      evaluationsRes.data.forEach(e => {
        const key = `${e.player_id}_${e.position_evaluated || 'default'}`;
        if (!evalsByPlayerAndPosition[e.player_id]) {
          evalsByPlayerAndPosition[e.player_id] = {};
        }
        const posEval = e.position_evaluated || playerMap[e.player_id]?.position || 'default';
        if (!evalsByPlayerAndPosition[e.player_id][posEval]) {
          evalsByPlayerAndPosition[e.player_id][posEval] = [];
        }
        evalsByPlayerAndPosition[e.player_id][posEval].push(e);
      });
      
      // Sort by average descending
      const sorted = averagesRes.data
        .filter(p => p.total_evaluations > 0)
        .map(p => ({
          ...p,
          position: playerMap[p.player_id]?.position,
          position2: playerMap[p.player_id]?.position2,
          evaluations_by_position: evalsByPlayerAndPosition[p.player_id] || {}
        }))
        .sort((a, b) => {
          const avgA = Object.values(a.averages || {}).reduce((sum, v) => sum + v, 0) / 7;
          const avgB = Object.values(b.averages || {}).reduce((sum, v) => sum + v, 0) / 7;
          return avgB - avgA;
        });
      setPlayers(sorted);
      setAllPlayers(playersRes.data);
    } catch (error) {
      toast.error(t('toasts.errorLoadingRanking'));
    } finally {
      setIsLoading(false);
    }
  };

  const getAverage = (averages) => {
    if (!averages) return 0;
    const values = Object.values(averages).filter(v => v > 0);
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  };

  // Separar: Levantadores SOMENTE na aba Levantadores, demais na aba Jogadores
  const regularPlayers = players.filter(p => {
    const mainPosition = p.position || p.player_position;
    return mainPosition !== "Levantador";
  });
  
  const setterPlayers = players.filter(p => {
    const mainPosition = p.position || p.player_position;
    return mainPosition === "Levantador";
  });

  const filteredRegularPlayers = regularPlayers.filter(p => {
    const pos = p.position || p.player_position;
    return positionFilter === "Todas" || pos === positionFilter;
  });

  const getMedalIcon = (index) => {
    if (index === 0) return <span className="text-3xl">🥇</span>;
    if (index === 1) return <span className="text-3xl">🥈</span>;
    if (index === 2) return <span className="text-3xl">🥉</span>;
    return <span className="text-xl font-bold text-muted-foreground">{index + 1}º</span>;
  };

  const getMedalBg = (index) => {
    if (index === 0) return "bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-yellow-500/50";
    if (index === 1) return "bg-gradient-to-r from-slate-400/20 to-slate-500/10 border-slate-400/50";
    if (index === 2) return "bg-gradient-to-r from-amber-600/20 to-amber-700/10 border-amber-600/50";
    return "bg-card/50 border-border/30";
  };

  const getScoreColor = (index) => {
    if (index === 0) return 'text-yellow-400';
    if (index === 1) return 'text-slate-300';
    if (index === 2) return 'text-amber-500';
    return 'text-green-400';
  };

  const getSetterCardBg = (index) => {
    if (index === 0) return "bg-gradient-to-r from-purple-500/20 to-purple-600/10 border-purple-500/50";
    if (index === 1) return "bg-gradient-to-r from-indigo-400/20 to-indigo-500/10 border-indigo-400/50";
    if (index === 2) return "bg-gradient-to-r from-violet-600/20 to-violet-700/10 border-violet-600/50";
    return "bg-card/50 border-border/30";
  };

  const getSetterScoreColor = (index) => {
    if (index === 0) return 'text-purple-400';
    if (index === 1) return 'text-indigo-300';
    if (index === 2) return 'text-violet-400';
    return 'text-purple-300';
  };

  const getPlayerName = (p) => p.first_name ? `${p.first_name} ${p.last_name}` : p.name;

  const handleCompare = async () => {
    if (!player1 || !player2) {
      toast.error(t('toasts.selectTwoPlayers'));
      return;
    }
    if (player1 === player2) {
      toast.error(t('toasts.selectDifferentPlayers'));
      return;
    }

    setIsComparing(true);
    try {
      const response = await axios.get(`${API}/players/compare/${player1}/${player2}`);
      setComparison(response.data);
    } catch (error) {
      toast.error(t('toasts.errorComparingPlayers'));
    } finally {
      setIsComparing(false);
    }
  };

  const getCompareBarWidth = (val1, val2) => {
    const max = Math.max(val1, val2, 1);
    return {
      width1: (val1 / 10) * 100,
      width2: (val2 / 10) * 100
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
                className="hover:bg-primary/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2">
                  <Trophy className="h-6 w-6 text-yellow-500" />
                  Ranking de Jogadores
                </h1>
              </div>
            </div>
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 max-w-lg">
                <TabsTrigger value="ranking" className="flex items-center gap-2">
                  <Trophy className="h-4 w-4" />
                  Jogadores
                </TabsTrigger>
                <TabsTrigger value="setters" className="flex items-center gap-2">
                  <Hand className="h-4 w-4" />
                  Levantadores
                </TabsTrigger>
                <TabsTrigger value="compare" className="flex items-center gap-2">
                  <GitCompare className="h-4 w-4" />
                  Comparar
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {activeTab === "ranking" && (
          <>
            {/* Filter */}
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={positionFilter} onValueChange={setPositionFilter}>
                <SelectTrigger className="w-36 bg-background/50 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POSITIONS.map(pos => (
                    <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground ml-2">
                {filteredRegularPlayers.length} jogador(es)
              </span>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-muted-foreground">Carregando ranking...</p>
              </div>
            ) : filteredRegularPlayers.length === 0 ? (
              <Card className="bg-card/60 border-border/50">
                <CardContent className="py-12 text-center">
                  <Trophy className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {positionFilter === "Todas" 
                      ? "Nenhum jogador avaliado ainda" 
                      : `Nenhum ${positionFilter} avaliado ainda`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredRegularPlayers.map((player, index) => {
                  const avg = getAverage(player.averages);
                  return (
                    <Card 
                      key={player.player_id}
                      className={`border transition-all duration-300 hover:shadow-lg ${getMedalBg(index)}`}
                      data-testid={`ranking-player-${index}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Medal/Position */}
                          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                            {getMedalIcon(index)}
                          </div>
                          
                          {/* Photo */}
                          <div className="w-14 h-14 rounded-full bg-slate-800 flex-shrink-0 overflow-hidden border-2 border-white/10">
                            {player.player_photo_url ? (
                              <img 
                                src={getPhotoUrl(player.player_photo_url)}
                                alt={player.player_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="h-6 w-6 text-slate-500" />
                              </div>
                            )}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-base truncate">
                              {player.player_name}
                            </h3>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant="outline" className="text-xs border-white/20">
                                {player.player_position}
                              </Badge>
                              {player.player_yankee && (
                                <Badge className="text-xs bg-primary/20 text-primary border-0">
                                  {player.player_yankee}
                                </Badge>
                              )}
                              <Badge className={`text-[10px] px-1.5 py-0 ${
                                (player.player_member_type || 'membro') === 'membro'
                                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                                  : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                              }`}>
                                {(player.player_member_type || 'membro') === 'membro' ? 'Membro' : 'Convidado'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {player.total_evaluations} aval.
                              </span>
                            </div>
                          </div>
                          
                          {/* Score */}
                          <div className="flex-shrink-0 text-right">
                            <div className={`text-2xl font-black ${getScoreColor(index)}`}>
                              {avg.toFixed(1)}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                              <TrendingUp className="h-3 w-3" />
                              média
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Setters Tab */}
        {activeTab === "setters" && (
          <>
            <div className="mb-4">
              <div className="flex items-center gap-2 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <Hand className="h-5 w-5 text-purple-400" />
                <span className="text-sm text-purple-300">
                  Ranking exclusivo de Levantadores com critérios específicos
                </span>
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-muted-foreground">Carregando ranking...</p>
              </div>
            ) : setterPlayers.length === 0 ? (
              <Card className="bg-card/60 border-border/50">
                <CardContent className="py-12 text-center">
                  <Hand className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Nenhum levantador avaliado ainda
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {setterPlayers.map((player, index) => {
                  const avg = getAverage(player.averages);
                  return (
                    <Card 
                      key={player.player_id}
                      className={`border transition-all duration-300 hover:shadow-lg ${getSetterCardBg(index)}`}
                      data-testid={`ranking-setter-${index}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Medal/Position */}
                          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                            {index === 0 ? <span className="text-3xl">🥇</span> :
                             index === 1 ? <span className="text-3xl">🥈</span> :
                             index === 2 ? <span className="text-3xl">🥉</span> :
                             <span className="text-xl font-bold text-muted-foreground">{index + 1}º</span>}
                          </div>
                          
                          {/* Photo */}
                          <div className="w-14 h-14 rounded-full bg-slate-800 flex-shrink-0 overflow-hidden border-2 border-purple-500/30">
                            {player.player_photo_url ? (
                              <img 
                                src={getPhotoUrl(player.player_photo_url)}
                                alt={player.player_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="h-6 w-6 text-slate-500" />
                              </div>
                            )}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-base truncate">
                              {player.player_name}
                            </h3>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge className="text-xs bg-purple-500/20 text-purple-300 border-0">
                                Levantador
                              </Badge>
                              {player.player_yankee && (
                                <Badge className="text-xs bg-primary/20 text-primary border-0">
                                  {player.player_yankee}
                                </Badge>
                              )}
                              <Badge className={`text-[10px] px-1.5 py-0 ${
                                (player.player_member_type || 'membro') === 'membro'
                                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                                  : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                              }`}>
                                {(player.player_member_type || 'membro') === 'membro' ? 'Membro' : 'Convidado'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {player.total_evaluations} aval.
                              </span>
                            </div>
                          </div>
                          
                          {/* Score */}
                          <div className="flex-shrink-0 text-right">
                            <div className={`text-2xl font-black ${getSetterScoreColor(index)}`}>
                              {avg.toFixed(1)}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                              <TrendingUp className="h-3 w-3" />
                              média
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
            
            <p className="text-xs text-muted-foreground text-center mt-4">
              {setterPlayers.length} levantador(es) no ranking
            </p>
          </>
        )}

        {activeTab === "compare" && (
          <div className="space-y-6">
            {/* Player Selection */}
            <Card className="bg-card/60 border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <GitCompare className="h-5 w-5 text-primary" />
                  Selecionar Jogadores
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Player 1 */}
                  <div className="space-y-3 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                    <h4 className="font-bold text-blue-400 text-sm uppercase tracking-wider">Jogador 1</h4>
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Nome</label>
                      <Select value={player1} onValueChange={(v) => {
                        setPlayer1(v);
                        const p = allPlayers.find(pl => pl.id === v);
                        if (p) setPosition1(p.position || "");
                      }}>
                        <SelectTrigger className="bg-input/50">
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {allPlayers
                            .sort((a, b) => getPlayerName(a).localeCompare(getPlayerName(b), 'pt-BR'))
                            .map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {getPlayerName(p)} - {p.position}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Posição para comparar</label>
                      <Select value={position1} onValueChange={setPosition1}>
                        <SelectTrigger className="bg-input/50">
                          <SelectValue placeholder="Posição..." />
                        </SelectTrigger>
                        <SelectContent>
                          {POSITION_OPTIONS.map((pos) => (
                            <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Player 2 */}
                  <div className="space-y-3 p-4 rounded-lg bg-green-500/5 border border-green-500/20">
                    <h4 className="font-bold text-green-400 text-sm uppercase tracking-wider">Jogador 2</h4>
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Nome</label>
                      <Select value={player2} onValueChange={(v) => {
                        setPlayer2(v);
                        const p = allPlayers.find(pl => pl.id === v);
                        if (p) setPosition2(p.position || "");
                      }}>
                        <SelectTrigger className="bg-input/50">
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {allPlayers
                            .sort((a, b) => getPlayerName(a).localeCompare(getPlayerName(b), 'pt-BR'))
                            .map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {getPlayerName(p)} - {p.position}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Posição para comparar</label>
                      <Select value={position2} onValueChange={setPosition2}>
                        <SelectTrigger className="bg-input/50">
                          <SelectValue placeholder="Posição..." />
                        </SelectTrigger>
                        <SelectContent>
                          {POSITION_OPTIONS.map((pos) => (
                            <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={handleCompare} 
                  className="w-full"
                  disabled={!player1 || !player2 || isComparing}
                >
                  <GitCompare className="h-4 w-4 mr-2" />
                  {isComparing ? "Comparando..." : "Comparar Jogadores"}
                </Button>
              </CardContent>
            </Card>

            {/* Comparison Result */}
            {comparison && (
              <Card className="bg-card/60 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Resultado da Comparação
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Player Headers with Photos, Names and Averages */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Player 1 Card */}
                    <div className="text-center p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                      <div className="w-20 h-20 rounded-full bg-slate-800 mx-auto mb-3 overflow-hidden border-3 border-blue-500/50">
                        {comparison.player1.photo_url ? (
                          <img 
                            src={getPhotoUrl(comparison.player1.photo_url)}
                            alt={comparison.player1.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="h-10 w-10 text-slate-500" />
                          </div>
                        )}
                      </div>
                      <p className="font-bold text-blue-400 text-lg">{comparison.player1.name}</p>
                      <Badge className="mt-1 bg-blue-500/20 text-blue-300 border-0">
                        {position1 || comparison.player1.position}
                      </Badge>
                      <div className="mt-3 p-2 bg-blue-500/20 rounded-lg">
                        <p className="text-3xl font-black text-blue-400">
                          {comparison.player1.overall_average?.toFixed(1) || "0.0"}
                        </p>
                        <p className="text-xs text-blue-300/70">MÉDIA GERAL</p>
                      </div>
                    </div>

                    {/* Player 2 Card */}
                    <div className="text-center p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                      <div className="w-20 h-20 rounded-full bg-slate-800 mx-auto mb-3 overflow-hidden border-3 border-green-500/50">
                        {comparison.player2.photo_url ? (
                          <img 
                            src={getPhotoUrl(comparison.player2.photo_url)}
                            alt={comparison.player2.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="h-10 w-10 text-slate-500" />
                          </div>
                        )}
                      </div>
                      <p className="font-bold text-green-400 text-lg">{comparison.player2.name}</p>
                      <Badge className="mt-1 bg-green-500/20 text-green-300 border-0">
                        {position2 || comparison.player2.position}
                      </Badge>
                      <div className="mt-3 p-2 bg-green-500/20 rounded-lg">
                        <p className="text-3xl font-black text-green-400">
                          {comparison.player2.overall_average?.toFixed(1) || "0.0"}
                        </p>
                        <p className="text-xs text-green-300/70">MÉDIA GERAL</p>
                      </div>
                    </div>
                  </div>

                  {/* Skills Comparison with Winner Indicator */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold uppercase tracking-widest text-muted-foreground text-center">
                      Comparação por Habilidade
                    </h4>
                    {SKILLS.map((skill) => {
                      const val1 = comparison.player1.averages?.[skill] || 0;
                      const val2 = comparison.player2.averages?.[skill] || 0;
                      const { width1, width2 } = getCompareBarWidth(val1, val2);
                      let winner = 0;
                      if (val1 > val2) winner = 1;
                      else if (val2 > val1) winner = 2;

                      let bgClass = 'bg-slate-800/50 border border-slate-700/50';
                      if (winner === 1) bgClass = 'bg-blue-500/10 border border-blue-500/30';
                      if (winner === 2) bgClass = 'bg-green-500/10 border border-green-500/30';

                      return (
                        <div key={skill} className={`p-3 rounded-lg ${bgClass}`}>
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-lg font-bold ${winner === 1 ? 'text-blue-400' : 'text-slate-400'}`}>
                                {val1.toFixed(1)}
                              </span>
                              {winner === 1 && <Trophy className="h-4 w-4 text-yellow-500" />}
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider text-white bg-slate-700/80 px-2 py-1 rounded">
                              {SKILL_LABELS[skill]}
                            </span>
                            <div className="flex items-center gap-2">
                              {winner === 2 && <Trophy className="h-4 w-4 text-yellow-500" />}
                              <span className={`text-lg font-bold ${winner === 2 ? 'text-green-400' : 'text-slate-400'}`}>
                                {val2.toFixed(1)}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1 h-4">
                            <div className="flex-1 bg-slate-900 rounded-l-full overflow-hidden">
                              <div 
                                className={`h-full rounded-l-full transition-all duration-500 ml-auto ${
                                  winner === 1 ? 'bg-blue-500' : 'bg-blue-500/50'
                                }`}
                                style={{ width: `${width1}%` }}
                              />
                            </div>
                            <div className="flex-1 bg-slate-900 rounded-r-full overflow-hidden">
                              <div 
                                className={`h-full rounded-r-full transition-all duration-500 ${
                                  winner === 2 ? 'bg-green-500' : 'bg-green-500/50'
                                }`}
                                style={{ width: `${width2}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Overall Winner */}
                  <div className={`text-center p-4 rounded-lg ${
                    comparison.player1.overall_average > comparison.player2.overall_average 
                      ? 'bg-blue-500/20 border border-blue-500/50' 
                      : comparison.player2.overall_average > comparison.player1.overall_average
                        ? 'bg-green-500/20 border border-green-500/50'
                        : 'bg-yellow-500/20 border border-yellow-500/50'
                  }`}>
                    <Trophy className="h-10 w-10 text-yellow-500 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground uppercase tracking-wider">Vencedor Geral</p>
                    <p className={`text-2xl font-black ${
                      comparison.player1.overall_average > comparison.player2.overall_average 
                        ? 'text-blue-400' 
                        : comparison.player2.overall_average > comparison.player1.overall_average
                          ? 'text-green-400'
                          : 'text-yellow-400'
                    }`}>
                      {comparison.player1.overall_average > comparison.player2.overall_average 
                        ? comparison.player1.name 
                        : comparison.player2.overall_average > comparison.player1.overall_average
                          ? comparison.player2.name
                          : 'EMPATE'}
                    </p>
                    {comparison.player1.overall_average !== comparison.player2.overall_average && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Diferença: {Math.abs(comparison.player1.overall_average - comparison.player2.overall_average).toFixed(1)} pontos
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
